import * as Icons from "@mui/icons-material";
import { LocalShipping } from "@mui/icons-material";
import { Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import { Duration } from "luxon";
import { useContext, useRef, useState } from "react";

import { getLocationsFromItemIds } from "@storyboard/web-client";
import { TestIds } from "@storyboard/web-test-ids/testIds";
import { ampli } from "@/config";
import { transport } from "@/config.ts";
import Locator from "@/locator.ts";
import type { FullItem } from "@/models/FeedContextProvider.tsx";
import { UxContext } from "@/models/UxStateProvider.tsx";
import theme from "@/theme/theme";
import { menuItemFocusSX } from "@/utils";

export default function FeedItemMenu({
  workspaceId,
  item,
  MenuOptions,
  setConfirmDeleteDialog,
  displayArtifact,
  setStatusModalOpen,
  textTranscript,
  buttonElement,
}: {
  workspaceId: string;
  item: FullItem;
  MenuOptions: any;
  displayArtifact?: string;
  setConfirmDeleteDialog: (value: boolean) => void;
  setStatusModalOpen: (value: boolean) => void;
  textTranscript: any;
  buttonElement?: React.ReactElement;
}) {
  const [fleetError, setFleetError] = useState<string>("");

  const handleCopy = (value, setter) => {
    setter(() => true);
    navigator.clipboard.writeText(value);
    setTimeout(() => setter(() => false), Duration.fromObject({ seconds: 3 }).toMillis());
  };
  const [copiedTranscript, setCopiedTranscript] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [disabled, setDisabled] = useState<boolean>(false);
  const {
    userReadOnlyMode,
    setFleetMessageActiveSection,
    setFleetMessageModalOpen,
    setFleetMessageLongitude,
    setFleetMessageLatitude,
    setFleetMessageFromItem,
    setFleetMessageText,
    setFleetMessageInvalidLocation,
  } = useContext(UxContext);

  const fetchGeoFromLocationIds = async () => {
    try {
      const fetchLocations = await getLocationsFromItemIds(transport, {
        workspaceId,
        itemIds: [item.id],
      });
      const matchLocations = fetchLocations?.locations?.find((location) => location.itemId === item.id);
      const found = matchLocations?.location ? matchLocations.location : null;
      if (found?.latitude && found?.longitude) {
        return found;
      }
      const message = "No Location Found";
      setFleetError(message);
      return Promise.reject(message);
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const sendFleetMessage = async () => {
    setDisabled(() => true);
    // The text doesn't depend on the location, so a message without one still seeds the composer (WEB-2325).
    setFleetMessageText(displayArtifact ?? "");
    setFleetMessageFromItem(true);
    try {
      const location = await fetchGeoFromLocationIds();
      setFleetMessageLatitude(location.latitude.toString());
      setFleetMessageLongitude(location.longitude.toString());
      // Counts only opens that found a location; the location-less path is deliberately not tracked.
      ampli.createFleetMessageFromDriverMessage();
    } catch (e) {
      console.error(e);
      setFleetMessageInvalidLocation(true);
    }

    setFleetMessageModalOpen(true);
    setFleetMessageActiveSection("geo-target");
    setDisabled(() => false);
  };

  const [open, setOpen] = useState<boolean>(false);

  const handleClick = () => {
    setOpen((value) => !value);
    ampli.viewMessageContextMenu();
  };

  const anchorEl = useRef<HTMLDivElement>();

  if (!MenuOptions.showOptions) {
    return <></>;
  }

  return (
    <Box sx={{ display: "flex" }} ref={anchorEl}>
      {buttonElement ? (
        <Box
          component="button"
          data-sb={TestIds.feed.itemMenu}
          aria-label={Locator.feed.items.actions.main}
          onClick={handleClick}
          sx={{ background: "none", border: "none", p: 0, cursor: "pointer", display: "flex" }}
        >
          {buttonElement}
        </Box>
      ) : (
        <Box sx={{ p: "8px" }}>
          <IconButton
            color="secondary"
            aria-label={Locator.feed.items.actions.main}
            onClick={handleClick}
            sx={{
              height: "100%",
              px: 0,
              py: 1,
              color: theme.palette.primary.main,
            }}
          >
            <Icons.MoreVert role="img" />
          </IconButton>
        </Box>
      )}

      <Menu
        anchorEl={anchorEl.current}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{
          mt: 1,
          ...menuItemFocusSX,
        }}
        open={open}
        onClose={() => setOpen(() => false)}
      >
        {MenuOptions.showMessageDetails ? (
          <MenuItem onClick={() => setStatusModalOpen(true)}>
            <ListItemIcon>
              <Icons.InfoOutlined role="img" />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontWeight: 500 }}>Message Details</ListItemText>
          </MenuItem>
        ) : null}
        {MenuOptions.canCopy ? (
          <MenuItem
            onClick={() => handleCopy(item?.url, setCopiedLink)}
            aria-label={Locator.feed.items.actions.copyLink}
          >
            <ListItemIcon>
              {copiedLink ? <Icons.Done role="presentation" /> : <Icons.Link role="presentation" />}
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontWeight: 500 }}>
              {copiedLink ? "Copied!" : "Copy Link"}
            </ListItemText>
          </MenuItem>
        ) : null}

        {MenuOptions.canCopyTranscript ? (
          <MenuItem onClick={() => handleCopy(textTranscript?.transcriptionContent, setCopiedTranscript)}>
            <ListItemIcon>
              {copiedTranscript ? <Icons.Done role="presentation" /> : <Icons.ContentCopy role="presentation" />}
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontWeight: 500 }}>
              {copiedTranscript ? "Copied!" : "Copy transcript"}
            </ListItemText>
          </MenuItem>
        ) : null}
        {MenuOptions.canDelete && !userReadOnlyMode && (
          <MenuItem
            onClick={() => {
              setConfirmDeleteDialog(true);
              setOpen(() => false);
            }}
            aria-label={Locator.feed.items.actions.delete}
          >
            <ListItemIcon>
              <Icons.Delete role="img" />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontWeight: 500 }}>Delete Message</ListItemText>
          </MenuItem>
        )}

        {MenuOptions.canSendFleetMessage && (
          <MenuItem
            data-sb={TestIds.feed.itemMenuFleetMessage}
            disabled={disabled || fleetError !== ""}
            onClick={() => sendFleetMessage()}
            aria-label={Locator.feed.items.actions.sendFleetMessage}
          >
            <ListItemIcon>
              <LocalShipping />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontWeight: 500 }}>
              {fleetError ? fleetError : "Send Fleet Message"}
            </ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
}
